<template>
    <RaterChrome :max-width="620" :show-anonymous="!invite?.is_authenticated">
        <div v-if="loading" class="body-small text-valence-grey-750">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="invite && cycleClosed">
            <!-- Cycle closed: no team icon or CTAs, just an explanation. -->
            <div class="landing-intro">
                <span class="landing-eyebrow body">Feedback request</span>
                <h1 class="display-serif text-valence-grey-850 leading-tight">This feedback cycle has closed</h1>
                <p class="landing-lede body-small">
                    The feedback period for {{ invite.subject_first_name }} has ended and responses are no longer being accepted. If you think this is a mistake, please reach out
                    to {{ invite.subject_first_name }} directly.
                </p>
            </div>
        </template>

        <template v-else-if="invite">
            <div class="landing-block">
                <div class="landing-intro">
                    <div class="landing-head">
                        <span class="landing-logo" aria-hidden="true">
                            <svg viewBox="0 0 38 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M18.0068 17.3582C18.4983 16.686 19.5017 16.686 19.9933 17.3582L34.0234 36.5431C34.6179 37.3561 34.0373 38.5 33.0301 38.5L4.96987 38.5C3.9627 38.5 3.38208 37.3561 3.97662 36.5431L18.0068 17.3582Z"
                                    fill="#5FA37C" />
                                <path
                                    d="M18.0068 1.35819C18.4983 0.685996 19.5017 0.685995 19.9933 1.35818L34.0234 20.5431C34.6179 21.3561 34.0373 22.5 33.0301 22.5L4.96987 22.5C3.9627 22.5 3.38208 21.3561 3.97662 20.5431L18.0068 1.35819Z"
                                    fill="#BEE59F" />
                            </svg>
                        </span>
                        <div class="landing-headtext">
                            <span class="landing-eyebrow body">Feedback request</span>
                            <h1 class="landing-title display-serif text-valence-grey-850">{{ invite.subject_first_name }} has requested feedback</h1>
                        </div>
                    </div>
                    <p class="landing-lede body-small">
                        Your answers help {{ invite.subject_first_name }} understand how they're doing and where to grow. We pool them with everyone else's before
                        {{ invite.subject_first_name }} sees them, so nothing is ever traced back to you.
                    </p>
                </div>

                <hr class="landing-divider" />

                <div class="landing-meta">
                    <div class="landing-meta__item">
                        <i class="bi bi-shield-lock landing-meta__icon" aria-hidden="true"></i>
                        <div class="landing-meta__text">
                            <span class="body-semibold text-valence-grey-850">Anonymous</span>
                            <span class="body-small text-valence-grey-750">Your name is never attached</span>
                        </div>
                    </div>
                    <span class="landing-meta__sep" aria-hidden="true"></span>
                    <div class="landing-meta__item">
                        <i class="bi bi-clock landing-meta__icon" aria-hidden="true"></i>
                        <div class="landing-meta__text">
                            <span class="body-semibold text-valence-grey-850">{{ questionCountTitle }}</span>
                            <span class="body-small text-valence-grey-750">About {{ estimatedMinutes }} minutes to complete</span>
                        </div>
                    </div>
                </div>
            </div>

            <BaseCard v-if="invite.personal_note" theme="accent" padding="md" class="italic text-valence-grey-850">
                "{{ invite.personal_note }}"
                <div class="not-italic body-small text-valence-grey-750 mt-2">— {{ invite.subject_first_name }}</div>
            </BaseCard>

            <BaseNotification v-if="alreadyResponded" theme="success" hide-dismiss text="You've already submitted feedback for this 360. Thank you!" />

            <!-- Existing-account handoff for external raters who already have Nadia logins (LAB-596). -->
            <BaseCard v-if="!invite.is_authenticated && invite.has_existing_account" theme="accent" padding="md" class="text-center">
                <p class="body-small text-valence-grey-850 mb-2">Have a Nadia account? Sign in to review your responses later.</p>
                <a :href="loginUrl" class="base-btn-secondary inline-flex items-center gap-1"> <i class="bi bi-box-arrow-in-right" aria-hidden="true"></i> Sign in </a>
            </BaseCard>

            <div v-if="!alreadyResponded" class="landing-actions">
                <a :href="beginUrl" class="base-btn-dark base-btn-sm inline-flex items-center" @click="onBeginClick"> Begin </a>
                <button type="button" :disabled="declining" class="landing-decline body-small-semibold text-valence-grey-850 disabled:opacity-50" @click="onDecline">
                    {{ declining ? "Declining…" : "Decline" }}
                </button>
            </div>

            <BaseNotification v-if="declineError" theme="error" :text="declineError" hide-dismiss />
        </template>
    </RaterChrome>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { computed, ref, watch } from "vue";

interface Props {
    invite_token: string;
    is_anonymous: boolean;
}

const props = defineProps<Props>();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

const { invite, loading, loadError } = useInviteLoader(props.invite_token);

watch(invite, (loaded) => {
    if (!loaded) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_LANDING_VIEWED, {
        already_responded: loaded.state === "responded",
        has_personal_note: loaded.personal_note.trim().length > 0,
        question_count: loaded.cycle_question_count,
    });
});

const cycleClosed = computed(() => invite.value?.cycle_state === "closed");
const alreadyResponded = computed(() => invite.value?.state === "responded");
const loginUrl = computed(() => `/accounts/login/?next=/reflections/360/r/${props.invite_token}`);
// When the subject already categorized this rater at nomination, the
// relationship step is skipped and Begin goes straight to the questions.
const beginUrl = computed(() => (invite.value?.relationship_prefilled ? `/reflections/360/r/${props.invite_token}/q/1` : `/reflections/360/r/${props.invite_token}/relationship`));
const questionCount = computed<number | null>(() => invite.value?.cycle_question_count ?? null);
const questionCountTitle = computed<string>(() => {
    const n = questionCount.value;
    if (n === null) {
        return "A few short questions";
    }
    return n === 1 ? "1 question" : `${n} questions`;
});
// Rough estimate — matches the "About 15 minutes" language used elsewhere for the default 3-question flow.
const estimatedMinutes = computed<number>(() => Math.max(5, Math.round((questionCount.value ?? 3) * 5)));

const declining = ref(false);
const declineError = ref<string | null>(null);

async function onDecline() {
    if (declining.value || !invite.value) {
        return;
    }
    if (!globalThis.confirm(`Decline this 360 invite from ${invite.value.subject_first_name}? Your name won't be shared.`)) {
        return;
    }
    declining.value = true;
    declineError.value = null;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${props.invite_token}/decline`).post().json();
        if (fetchError.value) {
            declineError.value = (data.value as { error?: string })?.error || "Could not decline. Please try again.";
            return;
        }
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_DECLINED);
        router.visit(`/reflections/360/r/${props.invite_token}/declined`);
    } finally {
        declining.value = false;
    }
}

function onBeginClick() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_BEGIN_CLICKED, {
        question_count: invite.value?.cycle_question_count ?? null,
    });
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

/* LAB-988 team-family greens (#5fa37c primary, #bee59f accent) — not yet in the
   Valence token palette, so they live here as scoped literals for the invite
   eyebrow, decline-hover, and team-icon fills. */
.landing-block {
    @apply flex flex-col gap-5 w-full;
}

.landing-intro {
    @apply flex flex-col items-start gap-1.5 w-full;
}

.landing-head {
    @apply flex items-start gap-2.5 w-full;
}

.landing-logo {
    @apply w-10 shrink-0;
}

.landing-logo svg {
    @apply block w-full h-auto;
    transform: rotate(180deg);
}

.landing-headtext {
    @apply flex flex-col gap-1 min-w-0;
}

.landing-eyebrow {
    color: #5fa37c;
}

.landing-title {
    @apply leading-tight;
}

.landing-lede {
    @apply text-valence-grey-750 w-full;
}

.landing-divider {
    @apply w-full border-0 border-t border-valence-cream-300 my-0;
}

.landing-meta {
    @apply flex items-stretch gap-5;
}

.landing-meta__item {
    @apply flex items-start gap-2.5;
}

.landing-meta__icon {
    @apply text-valence-grey-750 shrink-0 mt-1;
}

.landing-meta__text {
    @apply flex flex-col;
}

.landing-meta__sep {
    @apply w-px self-stretch bg-valence-cream-300 shrink-0;
}

.landing-actions {
    @apply flex items-center gap-2.5 mt-2;
}

.landing-decline {
    @apply bg-transparent border-0 cursor-pointer px-2 py-1 transition-colors;
}

.landing-decline:hover:not(:disabled) {
    color: #5fa37c;
}

@media (max-width: 640px) {
    .landing-meta {
        @apply flex-col gap-4;
    }
    .landing-meta__sep {
        @apply hidden;
    }
}
</style>
